import { Card, CardBody, CardHeader } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { pickBilingual } from '@lims/shared-types';
import { authApi } from '../api/endpoints.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { currentLocale } from '../i18n/index.js';

/**
 * The unauthenticated shell: sign-in, forgotten password, password reset.
 *
 * Shared so all three look like one product and none of them forgets the
 * language switcher — which has to be reachable BEFORE sign-in, since a user
 * whose interface is in the wrong language cannot sign in to change it.
 *
 * What it deliberately does not do is reveal anything. The laboratory's name
 * comes from a public endpoint that returns a name and nothing else: no user
 * list, no count, and no way to test whether an address is registered. There is
 * no navigation here, because there is nothing an unauthenticated visitor is
 * entitled to navigate to.
 */

export function PublicShell({
  title,
  subtitle,
  children,
  footer,
}: {
  /** Already translated. Falls back to the laboratory's own name. */
  readonly title?: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const branding = useQuery({
    queryKey: ['branding'],
    queryFn: authApi.branding,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const laboratoryName = branding.data?.tenant
    ? pickBilingual(
        { ar: branding.data.tenant.nameAr, en: branding.data.tenant.nameEn },
        locale,
      )
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-default-50">
      {/*
        `justify-end` resolves to the trailing edge, so the switcher sits top
        right in English and top left in Arabic — the near corner in both.
      */}
      <div className="flex justify-end px-4 pt-4 sm:px-6">
        <LanguageSwitcher />
      </div>

      <main
        id="main"
        className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6"
      >
        <div className="w-full max-w-md">
          <Card shadow="sm">
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-6">
              <h1 className="text-title font-semibold text-default-900">
                {title ?? laboratoryName ?? t('common.appName')}
              </h1>
              {subtitle ? (
                <p className="text-body text-default-600">{subtitle}</p>
              ) : null}
            </CardHeader>
            <CardBody className="gap-stack px-6 pb-6">{children}</CardBody>
          </Card>

          {footer ? (
            <div className="mt-stack text-center text-caption text-default-600">
              {footer}
            </div>
          ) : null}
        </div>
      </main>

      <footer className="px-4 pb-6 text-center text-caption text-default-600 sm:px-6">
        {laboratoryName ? (
          <p>{laboratoryName}</p>
        ) : null}
      </footer>
    </div>
  );
}
